import React from "react";
import { createRoot } from "react-dom/client";
import "./utils/i18n";

import App from "./App";
import { keycloakConfig } from "./providers/authProvider";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import { RefineSnackbarProvider } from "@refinedev/mui";
import { IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSnackbar, SnackbarKey } from "notistack";
import {
  setupGlobalErrorReporting,
  registerAccountContextProvider,
} from "./utils/discordNotifier";
import { useMarketplaceStore } from "./store/useMarketplaceStore";
import { config } from "./config/config";

setupGlobalErrorReporting();

const MAX_USER_AGENT_LENGTH = 140;

// sellerId + marketplace + account holder's name — the name is the same
// given_name/family_name already shown in the UI's own "welcome" header
// (UserMenu.tsx), so surfacing it in our internal Google Chat space isn't
// a new exposure, just makes it easier to tell whose session an error
// belongs to. User confirmed this explicitly (2026-07-11).
registerAccountContextProvider(() => {
  const claims = keycloakConfig.tokenParsed as
    | { sellerId?: string; given_name?: string; family_name?: string }
    | undefined;
  const { selectedCountry } = useMarketplaceStore.getState();
  const name = [claims?.given_name, claims?.family_name]
    .filter(Boolean)
    .join(" ");

  return {
    sellerId: claims?.sellerId,
    account: name || undefined,
    marketplace: selectedCountry
      ? `${selectedCountry.countryCode} (id=${selectedCountry.id})`
      : undefined,
    storeName: selectedCountry?.storeName,
    env: config.ENV,
    route: window.location.pathname,
    userAgent: navigator.userAgent.slice(0, MAX_USER_AGENT_LENGTH),
  };
});

const SnackbarCloseButton = ({ id }: { id: SnackbarKey }) => {
  const { closeSnackbar } = useSnackbar();
  return (
    <IconButton
      size="small"
      onClick={() => closeSnackbar(id)}
      sx={{ color: "inherit" }}
    >
      <Close fontSize="small" />
    </IconButton>
  );
};

/*
Sentry.init({
  dsn: "https://e393389e3e4da277bf1045fc755a91da@o4509848194449408.ingest.us.sentry.io/4509848240848896",
  sendDefaultPii: true,
  enableLogs: true,
  release: config.ENV,
});
*/

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

root.render(
  <ReactKeycloakProvider authClient={keycloakConfig}>
    <RefineSnackbarProvider action={(id) => <SnackbarCloseButton id={id} />}>
      <App />
    </RefineSnackbarProvider>
  </ReactKeycloakProvider>,
);
